import { useMemo } from "react";
import { toast } from "react-toastify";
import { Box } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import { useSummaryStyles } from "./summary.styles";
import { RootState } from "../../../../app/rootReducer";
import { fetchQuote } from "../../../../store/QuoteSlice";
import { removePromoCode } from "../../../../api/Quote/QuoteApi";
import { DeductionLines } from "./DeductionLines";
import { EQuoteDeductionCategory } from "../../../../api/Quote/EQuoteDeductionCategory";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { quoteVat, solarGrantAmountAvailable } from "../../../../app/utils";
import { PriceFormat } from "../../../../components/typography/PriceFormat";
import { EQuotePurchaserType } from "../../../../api/Types";

export const SummaryTotal = ({ quoteId }: { quoteId: string }) => {
  const classes = useSummaryStyles();
  const dispatch = useDispatch();
  const { isMoreThanSm } = useBreakpoint();
  const { quote, solarReport } = useSelector((state: RootState) => state.quote);

  const onDeletePromoCode = async (quoteDeductionId: string, promoCode: string | null) => {
    if (!quote) return;
    try {
      await removePromoCode(quote.id, quoteDeductionId);
      dispatch(fetchQuote(quoteId));
      toast.success(`Discount code ${promoCode} has been removed`);
    } catch (error: any) {
      const message = error?.message ?? error;
      toast.error(message);
    }
  };

  const hasRemoveButton = useMemo(() => {
    if (!quote) return false;
    return quote.deductions.some(
      (deduction) =>
        deduction.category === EQuoteDeductionCategory.Promotion ||
        deduction.category === EQuoteDeductionCategory.Referral ||
        deduction.category === EQuoteDeductionCategory.Discount
    );
  }, [quote]);

  const isSolar = quote?.type?.includes("Solar");

  const grantAmountAvailable = solarGrantAmountAvailable(quote, solarReport);

  return (
    <Box className={classes.summaryTotal}>
      {quote && (
        <Box mt={isMoreThanSm ? "30px" : "15px"}>
          <DeductionLines quote={quote} onDelete={onDeletePromoCode} hasRemoveButton={hasRemoveButton} />
        </Box>
      )}
      {isSolar ? (
        <>
          <Box className={`${classes.summaryTotalPayable} mt-0`}>
            <p>Total payable to Swyft Energy {quote?.purchaserType !== EQuotePurchaserType.Home && <>(incl. VAT)</>}</p>
            <span className={hasRemoveButton ? classes["mr-14"] : ""}>
              <PriceFormat
                value={quote?.price ?? 0}
                decimalScale={quote?.purchaserType === EQuotePurchaserType.Home ? 2 : 0}
              />
            </span>
          </Box>
          {solarReport && grantAmountAvailable ? (
            <Box className={classes.summaryTotalGrant}>
              <p>Total grants available</p>
              <span className={hasRemoveButton ? classes["mr-14"] : ""}>
                <PriceFormat value={grantAmountAvailable} prefix={"-€"} />
              </span>
            </Box>
          ) : null}
          <Box className={classes.summaryTotalCost}>
            <p>Final cost to you {quote?.purchaserType !== EQuotePurchaserType.Home && <>(excl. VAT)</>}</p>
            <span className={hasRemoveButton ? classes["mr-14"] : ""}>
              <PriceFormat
                value={Number(quote?.price ?? 0) - grantAmountAvailable - quoteVat(quote)}
                decimalScale={quote?.purchaserType === EQuotePurchaserType.Home ? 2 : 0}
              />
            </span>
          </Box>
        </>
      ) : (
        <Box className={classes.summaryTotalCostBoiler}>
          <p>Total payable to Swyft Energy</p>
          <span className={hasRemoveButton ? classes["mr-14"] : ""}>
            <PriceFormat value={quote?.price ?? 0} />
          </span>
        </Box>
      )}
    </Box>
  );
};
